import { useEffect, useRef, useState } from 'react';
import { subscribe } from '@jutro/events';
import { WMICTopics, WMICAmpUtil } from 'wmic-portals-utils-js';
import { useAuthentication } from 'wmic-digital-auth-react';
import { updateDataLayer } from '../utils/dataLayer';

function useUtagLoaded(maxRetries = 10, retryDelay = 500) {
    const [isLoaded, setIsLoaded] = useState(false);
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
        if (typeof window.utag !== 'undefined') {
            setIsLoaded(true);
            return;
        }

        if (retryCount < maxRetries) {
            const timer = setTimeout(() => {
                if (typeof window.utag !== 'undefined') {
                    setIsLoaded(true);
                } else {
                    setRetryCount((prev) => prev + 1);
                }
            }, retryDelay);

            return () => clearTimeout(timer);
        }

        return undefined;
    }, [retryCount, maxRetries, retryDelay]);

    return isLoaded;
}

const isBackForwardNavigation = () => {
    const navigationEntries = window.performance?.getEntriesByType?.('navigation') || [];
    if (navigationEntries.length > 0) {
        return navigationEntries[0].type === 'back_forward';
    }

    if (window.performance?.navigation) {
        return window.performance.navigation.type === 2;
    }

    return false;
};

const buildRouteUpdate = (evtData) => ({
    page: evtData.page || evtData.page_name || '',
    channel: evtData.channel || 'insurance',
    subchannel1: evtData.subchannel1 || 'account management',
    subchannel2: evtData.subchannel2 || 'servicing',
    category: evtData.category || ['insurance service'],
    product: evtData.product || ['account management'],
    quantity: evtData.quantity || ['1'],
    pageurl: window.location.href,
    platform: /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'Mobile' : 'Desktop',
    sessionid: sessionStorage.getItem('X_GWRE_SESSION') || '',
    state: (evtData.state || '').toLowerCase(),
    flow: evtData.flow || 'servicing'
});

function AdobeAnalyticsAMP() {
    const [isInitialized, setIsInitialized] = useState(false);
    const isUtagLoaded = useUtagLoaded();
    const { authHeader, userInfo: authUserData } = useAuthentication();
    const hasFetchedJurisdictionState = useRef(false);

    // window.wins_data_layer.state is only ever set by Quote-and-Buy flows; populate it once here
    // from the account contact record (auth/OIDC userInfo has no address) so every AMP tag resolves it.
    useEffect(() => {
        if (!authHeader || !authUserData || hasFetchedJurisdictionState.current) {
            return;
        }
        hasFetchedJurisdictionState.current = true;

        WMICAmpUtil.getAccountContact(authHeader, authUserData).then((res) => {
            const jurisdictionState = (res?.accountContact?.primaryAddress?.state || '').toString().toLowerCase();
            if (jurisdictionState) {
                updateDataLayer({ state: jurisdictionState });
            } else {
                hasFetchedJurisdictionState.current = false;
            }
        }).catch(() => {
            hasFetchedJurisdictionState.current = false;
        });
    }, [authHeader, authUserData]);

    useEffect(() => {
        if (!isUtagLoaded || isInitialized) {
            return;
        }

        subscribe(WMICTopics.ROUTE_CHANGED, (evtData = {}) => {
            try {
                const routeUpdate = buildRouteUpdate(evtData);

                console.log('[AdobeAnalyticsAMP] ROUTE_CHANGED data layer update', routeUpdate);

                if (window.updateDataLayer && typeof window.updateDataLayer === 'function') {
                    window.updateDataLayer(routeUpdate);
                }

                const routeViewMode = evtData.route_view_mode || 'always';
                const shouldFireView = routeViewMode === 'always'
                    || (routeViewMode === 'back_only' && isBackForwardNavigation());

                if (shouldFireView && window.utag && typeof window.utag.view === 'function') {
                    const viewPayload = { ...evtData };

                    console.log('[AdobeAnalyticsAMP] ROUTE_CHANGED utag.view payload', viewPayload);
                    window.utag.view(viewPayload);
                }
            } catch (err) {
                console.error('[AdobeAnalyticsAMP] Error in ROUTE_CHANGED handler:', err);
            }
        });

        subscribe(WMICTopics.CTA_CLICK, (evtData = {}) => {
            try {
                const linkPayload = { ...evtData };

                console.log('[AdobeAnalyticsAMP] CTA_CLICK utag.link payload', linkPayload);

                if (window.updateDataLayer && typeof window.updateDataLayer === 'function' && evtData.cta) {
                    window.updateDataLayer({ cta: evtData.cta });
                }

                if (window.utag && typeof window.utag.link === 'function') {
                    window.utag.link(linkPayload);
                }
            } catch (err) {
                console.error('[AdobeAnalyticsAMP] Error in CTA_CLICK handler:', err);
            }
        });

        subscribe(WMICTopics.FORM_ERROR, (evtData = {}) => {
            try {
                const errorPayload = { ...evtData };

                console.log('[AdobeAnalyticsAMP] FORM_ERROR utag.link payload', errorPayload);

                if (window.updateDataLayer && typeof window.updateDataLayer === 'function' && evtData.error) {
                    window.updateDataLayer({ error: evtData.error });
                }

                if (window.utag && typeof window.utag.link === 'function') {
                    window.utag.link(errorPayload);
                }
            } catch (err) {
                console.error('[AdobeAnalyticsAMP] Error in FORM_ERROR handler:', err);
            }
        });

        setIsInitialized(true);
    }, [isInitialized, isUtagLoaded]);

    return null;
}

export default AdobeAnalyticsAMP;
